import { Component, inject, OnDestroy, OnInit } from '@angular/core'
import { CommonModule } from '@angular/common'
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap'
import { combineLatest, fromEvent, from, map, Observable, of, startWith, Subject, switchMap, takeUntil } from 'rxjs'
import { Polygon } from 'ol/geom'
import { getArea } from 'ol/sphere'
import { CalepinageService } from '../../data-access/calepinage.service'
import { GeomapDomainService } from '../../domain/geomap.domain'

interface RecapStats {
    zoneIndex: number
    /** Numéro métier de la zone (l'index domaine 3 est la couche raccordement : zones 4-6 → indices 4-6). */
    numeroZone: number
    nbPanneaux: number
    surfaceAuSolM2: number
    surfacePvM2: number
    puissanceKwc: number
}

@Component({
    selector: 'app-calepinage-recap',
    standalone: true,
    imports: [CommonModule, NgbTooltipModule],
    templateUrl: './calepinage-recap.component.html',
    styleUrls: ['./calepinage-recap.component.scss'],
})
export class CalepinageRecapComponent implements OnInit, OnDestroy {
    private readonly geomapDomainService = inject(GeomapDomainService)
    private readonly calepinageService = inject(CalepinageService)
    private readonly destroy$ = new Subject<void>()

    stats$: Observable<RecapStats | null>
    /** Vrai dès qu'un calcul de calepinage est en cours (worker) → bandeau global centré en bas. */
    readonly calculating$: Observable<boolean> = this.calepinageService.calculating$.pipe(
        map(set => set.size > 0)
    )

    ngOnInit(): void {
        // Rechargés à chaque surcharge de paramètre modulable (parametresVersion$).
        const params$ = this.calepinageService.parametresVersion$.pipe(
            switchMap(() => from(this.calepinageService.loadParameters()))
        )
        this.stats$ = combineLatest([
            this.geomapDomainService.activeZoneId$,
            params$,
            this.calepinageService.typeGammePanneau$,
        ]).pipe(
            switchMap(([zoneIndex, params, gamme]) => {
                const dims = params.dimensionsPanneau[gamme]
                return this.statsForZone$(zoneIndex, dims.largeurM * dims.longueurM, params.puissancePanneauWc[gamme])
            }),
            takeUntil(this.destroy$)
        )
    }

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }

    private statsForZone$(zoneIndex: number | null, areaPerModuleM2: number, puissanceUnitaireWc: number): Observable<RecapStats | null> {
        if (zoneIndex == null) return of(null)
        const zone = this.geomapDomainService.getZone(zoneIndex)
        if (!zone) return of(null)
        const source = zone.panneauLayer.getSource()
        return fromEvent(source, 'change').pipe(
            map(() => this.computeStats(zoneIndex, areaPerModuleM2, puissanceUnitaireWc)),
            startWith(this.computeStats(zoneIndex, areaPerModuleM2, puissanceUnitaireWc))
        )
    }

    computeStats(zoneIndex: number, areaPerModuleM2: number, puissanceUnitaireWc: number): RecapStats {
        const numeroZone = zoneIndex <= 2 ? zoneIndex + 1 : zoneIndex
        const zone = this.geomapDomainService.getZone(zoneIndex)
        if (!zone) return { zoneIndex, numeroZone, nbPanneaux: 0, surfaceAuSolM2: 0, surfacePvM2: 0, puissanceKwc: 0 }
        const features = zone.panneauLayer.getSource().getFeatures()
        const nb = features.length
        const surfaceAuSol = features.reduce((acc, f) => {
            const geom = f.getGeometry()
            if (geom instanceof Polygon) return acc + getArea(geom)
            return acc
        }, 0)
        return {
            zoneIndex,
            numeroZone,
            nbPanneaux: nb,
            surfaceAuSolM2: surfaceAuSol,
            surfacePvM2: nb * areaPerModuleM2,
            puissanceKwc: nb * puissanceUnitaireWc / 1000,
        }
    }
}
